import { createContext, ReactNode, useContext, useState } from 'react';
import styled from 'styled-components';

import { Modal } from '../../../../../cdl/Modal/Modal';
import { TextButton } from '../../../../../common/buttons/TextButton';
import { translate } from '../../../../../common/helpers/translate.helper';
import { LabelText } from '../../../../../common/LabelText/LabelText';
import { Box } from '../../../../../common/ui/Box';
import { theme } from '../../../../../common/ui/theme';

type InvolvedPartyLayout = 'horizontal' | 'vertical';

export const InvolvedPartyLayoutContext = createContext<InvolvedPartyLayout>('horizontal');

const InvolvedPartyWrapper = styled.div<{ $layout: InvolvedPartyLayout }>`
    display: ${({ $layout }) => ($layout === 'vertical' ? 'contents' : 'flex')};
    flex-direction: column;
    justify-content: flex-start;
    padding-left: ${({ $layout }) => ($layout === 'vertical' ? 0 : theme.space[24])};
    padding-right: ${({ $layout }) => ($layout === 'vertical' ? 0 : theme.space[24])};
    border-left: ${({ $layout }) => ($layout === 'vertical' ? 'none' : `1px solid ${theme.colors.border.muted}`)};
    box-sizing: border-box;
    flex: 1 1 0;
    min-width: 0;

    &:first-of-type {
        padding-left: 0;
        border-left: none;
    }
`;

interface InvolvedPartyProps {
    label: string;
    disabled?: boolean;
    children?: ReactNode;
    modalContent?: ReactNode;
}

export const InvolvedParty = ({ label, children, modalContent, disabled = false }: InvolvedPartyProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const layout = useContext(InvolvedPartyLayoutContext);

    const viewDetailsLink = modalContent ? (
        <>
            <TextButton disabled={disabled} onClick={() => setIsOpen(true)}>
                {translate('global.coreData.viewDetails')}
            </TextButton>
            <Modal isOpen={isOpen} onDismiss={() => setIsOpen(false)}>
                {modalContent}
            </Modal>
        </>
    ) : null;

    if (layout === 'vertical') {
        return (
            <InvolvedPartyWrapper $layout={layout}>
                <LabelText style={{ whiteSpace: 'nowrap' }}>{label}</LabelText>
                <Box display="flex" flexDirection="column" minWidth={0}>
                    {children}
                    {viewDetailsLink}
                </Box>
            </InvolvedPartyWrapper>
        );
    }

    return (
        <InvolvedPartyWrapper $layout={layout}>
            <Box display="flex" flexDirection="column">
                <LabelText>{label}</LabelText>
                {children}
            </Box>

            {viewDetailsLink}
        </InvolvedPartyWrapper>
    );
};
